import React, { useEffect, useState } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Initialize Stripe - get publishable key from environment
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.error('❌ VITE_STRIPE_PUBLISHABLE_KEY is not set in environment variables');
  console.error('   Make sure it is set in Vercel environment variables and the site has been redeployed.');
} else {
  console.log('✅ Stripe publishable key loaded:', STRIPE_PUBLISHABLE_KEY.substring(0, 20) + '...');
}

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

interface CheckoutFormProps {
  type: string;
  name: string;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ type, name }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expressCheckoutAvailable, setExpressCheckoutAvailable] = useState(false);

  // Log Stripe initialization status
  useEffect(() => {
    if (stripe) {
      console.log('✅ Stripe is ready');
    } else {
      console.log('⏳ Waiting for Stripe to initialize...');
    }
  }, [stripe]);

  const handleExpressCheckout = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Handle express checkout payment
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/conversation?type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}&payment=success`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        setIsProcessing(false);
      } else {
        // Payment succeeded
        navigate(`/conversation?type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}&payment=success`);
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed');
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/conversation?type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}&payment=success`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        setIsProcessing(false);
      } else {
        navigate(`/conversation?type=${encodeURIComponent(type)}&name=${encodeURIComponent(name)}&payment=success`);
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-background px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/Assets/GhostQuillLogo.png" 
              alt="Cyrano" 
              className="w-9 h-9 object-contain"
            />
            <span className="font-serif text-[2rem] font-semibold text-space-cadet">
              Cyrano
            </span>
          </div>
          <h1 className="text-2xl font-semibold text-space-cadet mb-2">
            Complete Your Payment
          </h1>
          <p className="text-cool-gray">
            Secure payment for your {type} for {name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          {/* Express Checkout Element */}
          <div className="mb-6">
            <ExpressCheckoutElement
              onReady={({ availablePaymentMethods }) => {
                if (availablePaymentMethods && Object.keys(availablePaymentMethods).length > 0) {
                  setExpressCheckoutAvailable(true);
                }
              }}
              onConfirm={handleExpressCheckout}
              options={{
                buttonType: {
                  applePay: 'buy',
                  googlePay: 'buy',
                },
                buttonTheme: {
                  applePay: 'black',
                  googlePay: 'black',
                },
                buttonHeight: 48,
              }}
            />
          </div>

          {/* Divider */}
          {expressCheckoutAvailable && (
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-cool-gray/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-cool-gray">Or pay with card</span>
              </div>
            </div>
          )}

          {/* Payment Element (includes card and other methods like AfterPay if available) */}
          <form onSubmit={handleSubmit} id="payment-form">
            <div className="mb-6">
              <PaymentElement
                options={{
                  layout: 'tabs',
                }}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!stripe || !elements || isProcessing}
              className="w-full bg-space-cadet-secondary text-white py-3 px-6 rounded-lg font-medium hover:bg-space-cadet-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Processing...' : !stripe ? 'Loading payment form...' : `Pay $29.95 AUD`}
            </button>
          </form>

          {/* Security notice */}
          <p className="mt-6 text-sm text-cool-gray text-center">
            🔒 Your payment is secured by Stripe. We never store your card details.
          </p>
        </div>

        {/* Guest pass notice */}
        <div className="mt-6 text-center">
          <p className="text-sm text-cool-gray">
            Have a guest pass code? Enter it at checkout
          </p>
        </div>
      </div>
    </div>
  );
};

const CustomCheckout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const type = searchParams.get('type') || 'eulogy';
  const name = searchParams.get('name') || '';

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Cyrano - Complete Payment';
  }, []);

  useEffect(() => {
    const createCheckoutSession = async () => {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('Authentication required');
        }

        const response = await fetch('/api/stripe/create-onetime-checkout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type,
            name,
            uiMode: 'custom', // Request custom UI mode
          }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to create checkout session');
        }

        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else if (data.checkoutUrl) {
          // Fallback to redirect if custom mode not supported
          window.location.href = data.checkoutUrl;
          return;
        } else {
          throw new Error('No checkout session received');
        }
      } catch (err: any) {
        console.error('Checkout error:', err);
        setError(err.message || 'Failed to load checkout');
      } finally {
        setLoading(false);
      }
    };

    createCheckoutSession();
  }, [type, name, getToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-background flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/Assets/GhostQuillLogo.png" 
            alt="Cyrano" 
            className="w-12 h-12 mx-auto mb-4 animate-pulse"
          />
          <p className="text-cool-gray">Loading secure checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-warm-background flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.href = '/onboarding?step=3'}
            className="bg-space-cadet-secondary text-white py-2 px-6 rounded-lg hover:bg-space-cadet-primary transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return null;
  }

  if (!stripePromise) {
    return (
      <div className="min-h-screen bg-warm-background flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <p className="text-red-600 mb-4">Stripe is not configured. Please contact support.</p>
          <button
            onClick={() => window.location.href = '/onboarding?step=3'}
            className="bg-space-cadet-secondary text-white py-2 px-6 rounded-lg hover:bg-space-cadet-primary transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#1a2540', // space-cadet-secondary
        colorBackground: '#ffffff',
        colorText: '#1a2540',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm type={type} name={name} />
    </Elements>
  );
};

export default CustomCheckout;
